import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

interface AdvancedSEOProps {
  page: string;
  additionalKeywords?: string[];
  enableWebVitals?: boolean;
  enableHeatmap?: boolean;
  structuredDataExtensions?: object[];
}

const AdvancedSEO: React.FC<AdvancedSEOProps> = ({
  page,
  additionalKeywords = [],
  enableWebVitals = true,
  enableHeatmap = false,
  structuredDataExtensions = []
}) => {
  
  useEffect(() => {
    // Core Web Vitals tracking
    if (enableWebVitals && typeof window !== 'undefined') {
      // Track loading performance
      const observer = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          // Track Core Web Vitals
          if (entry.entryType === 'navigation') {
            const navigationEntry = entry as PerformanceNavigationTiming;
            console.log('Page Load Performance:', {
              page,
              fcp: navigationEntry.loadEventEnd - navigationEntry.fetchStart,
              lcp: navigationEntry.loadEventEnd - navigationEntry.fetchStart,
              cls: 0, // Would be calculated differently in real implementation
            });
          }
        });
      });
      
      observer.observe({ entryTypes: ['navigation'] });
      
      // Track user engagement
      const trackEngagement = () => {
        const timeOnPage = Date.now() - performance.timeOrigin;
        console.log(`User engagement on ${page}:`, { timeOnPage });
      };
      
      // Track scroll depth
      let maxScroll = 0;
      const trackScroll = () => {
        const scrolled = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
        if (scrolled > maxScroll) {
          maxScroll = scrolled;
        }
      };
      
      window.addEventListener('scroll', trackScroll);
      window.addEventListener('beforeunload', trackEngagement);
      
      return () => {
        observer.disconnect();
        window.removeEventListener('scroll', trackScroll);
        window.removeEventListener('beforeunload', trackEngagement);
      };
    }
  }, [page, enableWebVitals]);

  // Enhanced BreadcrumbList schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://dailzy.com/"
      },
      ...(page !== 'home' ? [{
        "@type": "ListItem",
        "position": 2,
        "name": page.charAt(0).toUpperCase() + page.slice(1),
        "item": `https://dailzy.com/${page === 'home' ? '' : page}`
      }] : [])
    ]
  };

  // Enhanced SiteNavigationElement schema
  const siteNavigationSchema = {
    "@context": "https://schema.org",
    "@type": "SiteNavigationElement",
    "name": "Main Navigation",
    "url": "https://dailzy.com",
    "hasPart": [
      {
        "@type": "WebPage",
        "name": "Home",
        "url": "https://dailzy.com/"
      },
      {
        "@type": "WebPage",
        "name": "About",
        "url": "https://dailzy.com/about"
      },
      {
        "@type": "WebPage",
        "name": "Vendors",
        "url": "https://dailzy.com/vendors"
      },
      {
        "@type": "WebPage",
        "name": "Blog",
        "url": "https://dailzy.com/blog"
      },
      {
        "@type": "WebPage",
        "name": "Contact",
        "url": "https://dailzy.com/contact"
      }
    ]
  };

  // Enhanced SearchAction schema
  const searchActionSchema = {
    "@context": "https://schema.org",
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://dailzy.com/search?q={search_term_string}",
      "actionPlatform": [
        "https://schema.org/DesktopWebPlatform",
        "https://schema.org/MobileWebPlatform"
      ]
    },
    "query-input": {
      "@type": "PropertyValueSpecification",
      "valueRequired": true,
      "valueName": "search_term_string"
    }
  };

  // Enhanced VideoObject schema for future video content
  const videoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": "How Dailzy Works - Quick Commerce Explained",
    "description": "Learn how Dailzy delivers your daily essentials in just 10-20 minutes",
    "thumbnailUrl": "https://dailzy.com/video-thumbnail.jpg",
    "uploadDate": "2024-01-01T00:00:00Z",
    "duration": "PT2M30S",
    "embedUrl": "https://dailzy.com/video/how-it-works",
    "publisher": {
      "@type": "Organization",
      "name": "Dailzy",
      "logo": {
        "@type": "ImageObject",
        "url": "https://dailzy.com/lovable-uploads/8936458b-f120-42aa-a567-eac1e09a8296.png"
      }
    },
    "potentialAction": {
      "@type": "SeekToAction",
      "target": "https://dailzy.com/video/how-it-works?t={seek_to_second_number}",
      "startOffset-input": "required name=seek_to_second_number"
    }
  };

  // Enhanced Review schema for social proof
  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "Review",
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": "5",
      "bestRating": "5"
    },
    "author": {
      "@type": "Person",
      "name": "Verified Customer"
    },
    "reviewBody": "Dailzy has transformed how I shop. Getting groceries in 15 minutes is incredible!",
    "datePublished": "2024-01-15",
    "publisher": {
      "@type": "Organization",
      "name": "Dailzy"
    }
  };

  // Enhanced Event schema for promotions/launches
  const eventSchema = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Dailzy Launch Event",
    "startDate": "2024-02-01T00:00:00Z",
    "endDate": "2024-02-28T23:59:59Z",
    "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
    "eventStatus": "https://schema.org/EventScheduled",
    "location": {
      "@type": "VirtualLocation",
      "url": "https://dailzy.com"
    },
    "description": "Join us for the official launch of Dailzy quick commerce platform",
    "offers": {
      "@type": "Offer",
      "url": "https://dailzy.com/signup",
      "price": "0",
      "priceCurrency": "INR",
      "availability": "https://schema.org/InStock",
      "validFrom": "2024-01-01T00:00:00Z"
    },
    "organizer": {
      "@type": "Organization",
      "name": "Dailzy",
      "url": "https://dailzy.com"
    }
  };

  return (
    <Helmet>
      {/* Advanced robots directives */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1, noarchive" />
      
      {/* Enhanced hreflang for international SEO */}
      <link rel="alternate" hrefLang="en" href={`https://dailzy.com/${page === 'home' ? '' : page}`} />
      <link rel="alternate" hrefLang="hi" href={`https://hi.dailzy.com/${page === 'home' ? '' : page}`} />
      <link rel="alternate" hrefLang="x-default" href={`https://dailzy.com/${page === 'home' ? '' : page}`} />
      
      {/* Advanced meta tags for search engines */}
      <meta name="rating" content="general" />
      <meta name="revisit-after" content="3 days" />
      <meta name="distribution" content="global" />
      <meta name="expires" content="never" />
      <meta name="pragma" content="no-cache" />
      
      {/* Enhanced social media meta tags */}
      <meta property="og:updated_time" content={new Date().toISOString()} />
      <meta property="og:see_also" content="https://dailzy.com/about" />
      <meta property="og:see_also" content="https://dailzy.com/vendors" />
      
      {/* Twitter Cards enhancements */}
      <meta name="twitter:label1" content="Service" />
      <meta name="twitter:data1" content="Quick Commerce" />
      <meta name="twitter:label2" content="Delivery Time" />
      <meta name="twitter:data2" content="10-20 minutes" />
      
      {/* Schema.org structured data */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(siteNavigationSchema)}
      </script>
      
      <script type="application/ld+json">
        {JSON.stringify(searchActionSchema)}
      </script>
      
      {/* Conditional schemas based on page */}
      {page === 'home' && (
        <>
          <script type="application/ld+json">
            {JSON.stringify(videoSchema)}
          </script>
          <script type="application/ld+json">
            {JSON.stringify(reviewSchema)}
          </script>
          <script type="application/ld+json">
            {JSON.stringify(eventSchema)}
          </script>
        </>
      )}
      
      {/* Additional structured data extensions */}
      {structuredDataExtensions.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
      
      {/* Advanced performance hints */}
      <link rel="dns-prefetch" href="//fonts.googleapis.com" />
      <link rel="dns-prefetch" href="//www.google-analytics.com" />
      <link rel="dns-prefetch" href="//www.googletagmanager.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      
      {/* Resource hints for critical assets */}
      <link rel="preload" href="/lovable-uploads/8936458b-f120-42aa-a567-eac1e09a8296.png" as="image" />
      
      {/* Enhanced security headers via meta tags */}
      <meta httpEquiv="Content-Security-Policy" content="default-src 'self'; script-src 'self' 'unsafe-inline' https://www.googletagmanager.com; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src 'self' https://fonts.gstatic.com; img-src 'self' data: https:; connect-src 'self' https://www.google-analytics.com;" />
      
      {/* Progressive Web App enhancements */}
      <meta name="application-name" content="Dailzy" />
      <meta name="msapplication-tooltip" content="Quick Commerce Platform" />
      <meta name="msapplication-starturl" content="/" />
      <meta name="msapplication-navbutton-color" content="#ef4444" />
      <meta name="msapplication-window" content="width=1024;height=768" />
      
      {/* Additional keywords for current page */}
      {additionalKeywords.length > 0 && (
        <meta name="keywords" content={additionalKeywords.join(', ')} />
      )}
      
      {/* Heatmap tracking (conditional) */}
      {enableHeatmap && (
        <script type="text/javascript">
          {`
            // Hotjar or similar heatmap tracking would go here
            console.log('Heatmap tracking enabled for page: ${page}');
          `}
        </script>
      )}
    </Helmet>
  );
};

export default AdvancedSEO; 